import { makeStyles } from 'tss-react/mui';
import { useTheme } from '@mui/material/styles';
import { AxiosError, AxiosResponse } from 'axios';
import React, { useEffect, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { MutationStatus, UseMutateFunction } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';
import {
  ApiButton,
  Box,
  Button,
  CustomSelect,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  MUIDataTable,
  MUIDataTableColumnDef,
  SearchBar,
  TableActionTypes,
  TableState,
  TABLE_INITIAL_STATE,
  Typography,
  useDataTable,
  useSnackbarHandler,
  MUIDataTableColumn
} from 'web-library';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { candidateService, GenericErrorResponse } from '../../api';
import { usePostExamSubjectRegistrationDraft } from '../../api/candidate/exam/@candidate/queries-services/queries';
import {
  usePostAddedCandidates,
  usePostCandidates,
} from '../../api/candidate/exam/@coordinator/queries-services/queries';
import {
  ExamSaveSubjectRegistrationDraftRequest,
  ExamSubjects,
  ExamType,
  GroupExamWithdrawalCandidate,
  GroupExamWithdrawalCandidateRequest,
} from '../../api/candidate/exam/model';
import { dropDownMappingDescriptionAsValue, useAPIDropdown } from '../../hooks/dropdown';
import { customFilterChipRender } from '../../hooks/table';
import { rowsPerPageOptionsDefault } from './logic/useWithdrawalDataTable';

const useStyles = makeStyles()((theme) =>
({
  dialog: {
    overflow: 'hidden',
  },
  searchContainer: {
    paddingBottom: theme.spacing(2),
  },
  select: {
    marginBottom: theme.spacing(3),
  },
  addIcon: {
    verticalAlign: 'sub',
    marginRight: 10,
    color: theme.color.icon.iconInverse,
  },
  buttonContainer: {
    marginTop: theme.spacing(2),
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

interface ISelectSubjectsHooks {
  history: any;
  t: TFunction<'translation'>;
  mutatePostExamSubjectRegistrationDraft: UseMutateFunction<
    AxiosResponse,
    AxiosError<AxiosError<GenericErrorResponse>>,
    ExamSaveSubjectRegistrationDraftRequest
  >;
  mutatePostExamSubjectRegistrationDraftStatus: MutationStatus;
  applicationUuid: string;
  tableDispatch: React.Dispatch<TableActionTypes>;
}

interface ISelectSubjectsStyling {
  classes: any;
}

interface ITableColumns {
  data: ISelectSubjectsData;
  styling: ISelectSubjectsStyling;
  hooks: ISelectSubjectsHooks;
}

interface ISelectSubjectsData {
  subjectListData: ExamSubjects[];
  examSeries: { functionCode: any; typeMapping: { [key: string]: any } };
  examLevels: { functionCode: any; typeMapping: { [key: string]: any } };
  state: TableState;
}

const examLevelColumn = (
  data: ISelectSubjectsData,
  styling: ISelectSubjectsStyling,
  hooks: ISelectSubjectsHooks
): MUIDataTableColumnDef => {
  const {
    examLevels: { functionCode: filterCodeExamLevel, typeMapping: filterMappingExamLevel },
    state,
  } = data;

  const { t, tableDispatch } = hooks;
  const { classes } = styling;

  return {
    name: 'examLevel',
    label: t('exam->>SelectSubjects.ExamLevel'),
    options: {
      sort: true,
      searchable: true,
      filter: true,
      filterType: 'custom',
      setCellProps: () => ({ style: { width: '23%' } }),
      customBodyRenderLite: (dataIndex: number) => (
        <Box className={classes.subjectContainer}>
          {state.data[dataIndex].examLevel.map((exam: string, examIndex: number) => (
            <Typography className={classes.subjectText} key={dataIndex} variant="body2">
              {exam}
              {examIndex < state.data[dataIndex].examLevel.length - 1 && <>,</>}
            </Typography>
          ))}
        </Box>
      ),
      customFilterListOptions: {
        render: (v: any) => [filterMappingExamLevel[v[0]].children],
        update: (filterList: string[][],
          filterPos: number,
          index: number) => {
          // update table filter state
          tableDispatch({
            type: 'UPDATE_FILTERS',
            payload: { ...state.filters, examLevel: [] },
          });

          // update table inner filter
          return customFilterChipRender(filterList, filterPos, index);
        },
      },
      filterOptions: {
        fullWidth: true,
        display: (filterList: string[][], onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void, index: number, column: MUIDataTableColumn) => (
          <CustomSelect
            className={classes.select}
            // eslint-disable-next-line react/destructuring-assignment
            value={filterList[index][0]}
            displayEmpty
            menuItems={filterCodeExamLevel}
            onChange={(event) => {
              // eslint-disable-next-line no-param-reassign
              filterList[index][0] = event.target.value as string;
              onChange(filterList[index], index, column);
            }}
            label={t('exam->>SelectSubjects.ExamLevel')}
            labelId="inputSelectLabel-examLevel"
            id="inputSelect-examLevel"
          />
        ),
      },
    },
  };
};

const examSeriesColumn = (
  data: ISelectSubjectsData,
  styling: ISelectSubjectsStyling,
  hooks: ISelectSubjectsHooks
): MUIDataTableColumnDef => {
  const {
    examSeries: { functionCode: filterCodeExamSeries, typeMapping: filterMappingExamSeries },
    state,
  } = data;
  const { t, tableDispatch } = hooks;
  const { classes } = styling;

  return {
    name: 'examSeries',
    label: t('exam->>SelectSubjects.ExamSeries'),
    options: {
      sort: true,
      searchable: true,
      filter: true,
      filterType: 'custom',
      setCellProps: () => ({ style: { width: '20%' } }),
      customFilterListOptions: {
        render: (v: any) => [filterMappingExamSeries[v[0]].children],
        update: (filterList: string[][],
          filterPos: number,
          index: number) => {
          // update table filter state
          tableDispatch({
            type: 'UPDATE_FILTERS',
            payload: { ...state.filters, examSeries: [] },
          });

          // update table inner filter
          return customFilterChipRender(filterList, filterPos, index);
        },
      },
      filterOptions: {
        fullWidth: true,
        display: (filterList: string[][], onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void, index: number, column: MUIDataTableColumn) => (
          <CustomSelect
            className={classes.select}
            // eslint-disable-next-line react/destructuring-assignment
            value={filterList[index][0]}
            displayEmpty
            menuItems={filterCodeExamSeries}
            onChange={(event) => {
              // eslint-disable-next-line no-param-reassign
              filterList[index][0] = event.target.value as string;
              onChange(filterList[index], index, column);
            }}
            label={t('exam->>SelectSubjects.ExamSeries')}
            labelId="inputSelectLabel-examSeries"
            id="inputSelect-examSeries"
          />
        ),
      },
    },
  };
};

const tableColumns = ({ data, styling, hooks }: ITableColumns): MUIDataTableColumnDef[] => {
  const { state } = data;
  const { t } = hooks;
  const { classes } = styling;

  return [
    {
      name: 'candidateId',
      label: t('withdrawal->>Coordinator.SelectCandidates.NRIC'),
      options: {
        sort: true,
        searchable: true,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Box className={classes.subjectContainer}>
            <Typography className={classes.subjectText} variant="body2">
              {state.data[dataIndex].candidateId}
            </Typography>
          </Box>
        ),
      },
    },
    {
      name: 'candidateName',
      label: t('withdrawal->>Coordinator.SelectCandidates.CandidateName'),
      options: {
        sort: true,
        searchable: true,
        filter: false,
        setCellProps: () => ({ style: { width: '25%' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <Box>
            <Typography variant="body2">{state.data[dataIndex].name}</Typography>
          </Box>
        ),
      },
    },
    examLevelColumn(data, styling, hooks),
    examSeriesColumn(data, styling, hooks),
  ];
};

const useRawFilters = (examTypeCode: string) => {
  const examLevels = useAPIDropdown(
    () => candidateService.exam.candidate.usePostExamLevel({ examType: examTypeCode }),
    true,
    dropDownMappingDescriptionAsValue
  );

  const examSeries = useAPIDropdown(
    () => candidateService.exam.candidate.useGetExamSeries(),
    true,
    dropDownMappingDescriptionAsValue
  );

  return { examLevels, examSeries };
};

const useCallMutates = (
  applicationUuid: string,
  tableDispatch: React.Dispatch<TableActionTypes>,
  state: TableState
) => {
  const { mutate: mutatePostCandidates } = usePostCandidates(
    (res: { data: { content: GroupExamWithdrawalCandidate[]; totalElements: number } }) => {
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: res.data.content,
          count: res.data.totalElements,
        },
      } as TableActionTypes);
    },
    () => {
      // do nothing
    },
    () => {
      // do nothing
    }
  );

  useEffect(() => {
    mutatePostCandidates({
      uuid: applicationUuid,
      q: state.search || null,
      examLevel: state.filters.examLevel && state.filters.examLevel[0],
      examSeries: state.filters.examSeries && state.filters.examSeries[0],
      apiPathParam: {
        page: state.page,
        pageSize: state.size,
        sort: state.sortColumn,
        order: state.order,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.order,
    state.filters.examLevel,
    state.filters.examSeries,
    state.sortColumn,
    state.page,
    state.size,
    state.search,
    applicationUuid,
  ]);

  const {
    mutate: mutatePostExamSubjectRegistrationDraft,
    status: mutatePostExamSubjectRegistrationDraftStatus,
    reset: mutatePostExamSubjectRegistrationDraftReset,
  } = usePostExamSubjectRegistrationDraft(
    () => {
      // do nothing
    },
    () => {
      // do nothing
    },
    () => {
      mutatePostExamSubjectRegistrationDraftReset();
    }
  );

  return {
    mutatePostExamSubjectRegistrationDraft,
    mutatePostExamSubjectRegistrationDraftStatus,
    mutatePostExamSubjectRegistrationDraftReset,
  };
};

interface AddCandidateModalProps {
  modalState: boolean;
  onDialogClose: () => void;
  mutatePostSearchWithdrawalCandidates: (variables: GroupExamWithdrawalCandidateRequest) => void;
  examTypeCode: ExamType;
}

const AddCandidatesModal = ({
  modalState,
  onDialogClose,
  mutatePostSearchWithdrawalCandidates,
  examTypeCode,
}: AddCandidateModalProps) => {
  const { classes } = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const { uuid: applicationUuid } = useParams<any>();
  const history = useHistory();
  const [selectedCandidates, setSelectedCandidates] = useState<GroupExamWithdrawalCandidate[]>([]);
  const [errorMessage, setErrorMessage] = useState('');

  const { snackbarHandler } = useSnackbarHandler();

  const { state, dispatch: tableDispatch, tOptions } = useDataTable({
    ...TABLE_INITIAL_STATE,
    sortColumn: 'candidateName',
    order: 'asc',
    size: 10,
    page: 0,
    search: '',
    filters: {},
  });

  const { examLevels, examSeries } = useRawFilters(examTypeCode);

  const {
    mutatePostExamSubjectRegistrationDraft,
    mutatePostExamSubjectRegistrationDraftStatus,
  } = useCallMutates(applicationUuid, tableDispatch, state);

  const {
    mutate: mutatePostAddedCandidates,
    status: postAddedCandidatesStatus,
  } = usePostAddedCandidates(
    () => {
      mutatePostSearchWithdrawalCandidates({
        uuid: applicationUuid,
        apiPathParam: {
          page: 0,
          pageSize: 10,
        },
      });
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('withdrawal->>Coordinator.SelectCandidates.AddCandidatesToList', {
          candidateCount: selectedCandidates.length.toString(),
        }),
      });
      onDialogClose();
    },
    (res) => {
      if (res.response?.data?.code === '1604') {
        setErrorMessage(
          t('withdrawal->>Coordinator.SelectCandidates.CandidateRegistrationIsProcessing')
        );
      }
    },
    () => {
      // do nothing
    }
  );

  useEffect(() => {
    setSelectedCandidates(Object.values(state.rowsSelected));
  }, [state.rowsSelected]);

  useEffect(() => {
    tableDispatch({
      type: 'CLEAR_SELECTION',
    } as TableActionTypes);
  }, [tableDispatch, state.filters, state.search]);

  const handleAddCandidates = () => {
    const uuidArray: Array<{ uuid: string }> = [];
    selectedCandidates.forEach((candidate: GroupExamWithdrawalCandidate) => {
      const uuidObject = { uuid: candidate.uuid };
      uuidArray.push(uuidObject);
      return uuidArray;
    });

    mutatePostAddedCandidates({
      applicationUuid,
      uuidArray,
    });
  };

  return (
    <>
      <Dialog
        id="group-withdrawal-add-candidates-dialog"
        fullWidth
        open={modalState}
        maxWidth="md"
        PaperProps={{ className: classes.dialog }}
      >
        <DialogTitle>{t('withdrawal->>Coordinator.SelectCandidates.AddCandidate')}</DialogTitle>
        <DialogContent>
          <Box className={classes.searchContainer}>
            <SearchBar
              placeholder={t('withdrawal->>Coordinator.SelectCandidates.SearchCandidates')}
              name="searchInput"
              ariaLabel="searchInput"
              defaultValue={state.search}
              onSearch={(searchStr: string) => {
                tableDispatch({
                  type: 'UPDATE_SEARCH',
                  payload: searchStr.trim(),
                });
              }}
            />
          </Box>

          <Box>
            <MUIDataTable
              // @ts-ignore
              state={state}
              dispatch={tableDispatch}
              columns={tableColumns({
                data: {
                  subjectListData: state.data,
                  examSeries,
                  examLevels,
                  state,
                },
                styling: {
                  classes,
                },
                hooks: {
                  history,
                  t,
                  mutatePostExamSubjectRegistrationDraft,
                  mutatePostExamSubjectRegistrationDraftStatus,
                  applicationUuid,
                  tableDispatch,
                },
              })}
              // @ts-ignore
              data={state.data}
              options={{
                ...tOptions,
                filter: ExamType.GCE === examTypeCode,
                pagination: true,
                sort: true,
                selectToolbarPlacement: 'none',
                selectableRows: 'multiple',
              }}
              rowsPerPageOptions={rowsPerPageOptionsDefault}
            />
          </Box>
          {errorMessage && (
            <Typography variant="body2" color="error">
              {errorMessage}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Box className={classes.buttonContainer}>
            <Button
              id="button-cancel-add-candidate"
              type="button"
              variant="text"
              onClick={onDialogClose}
            >
              {t('withdrawal->>Coordinator.SelectCandidates.Cancel')}
            </Button>
            {selectedCandidates.length === 0 ? (
              <ApiButton
                id="button-disable-add"
                type="submit"
                status="idle"
                disabled={selectedCandidates.length === 0}
              >
                <Icon
                  className={classes.addIcon}
                  size="M"
                  name={IconName.add}
                  color={theme.color.icon.iconInverse}
                />
                {t('withdrawal->>Coordinator.SelectCandidates.AddCandidates')}
              </ApiButton>
            ) : (
              <ApiButton
                id="button-enable-add"
                type="submit"
                status={postAddedCandidatesStatus}
                onClick={handleAddCandidates}
              >
                <Icon
                  className={classes.addIcon}
                  size="M"
                  name={IconName.add}
                  color={theme.color.icon.iconInverse}
                />
                {t(`withdrawal:SelectCandidates.Add {{candidateCount}} Candidate(s)`, {
                  candidateCount: selectedCandidates.length,
                })}
              </ApiButton>
            )}
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddCandidatesModal;
